import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Grid,
  Input,
  List,
  Modal,
  Pagination,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  notification,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import type { ColumnsType } from "antd/es/table";
import {
  CloudDownloadOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import api from "shared/api/client";
import "./CanvasOrdersPage.css";

interface CanvasItem {
  _id: string;
  size?: string;
  code?: string;
}

interface CanvasFile {
  _id: string;
  originalName: string;
  size: number;
}

interface CanvasOrder {
  _id: string;
  year: string;
  day: string;
  bookedTime: string;
  packageName: string;
  description?: string;
  canvasRepairNote?: string;
  canvas: CanvasItem[];
  customer?: {
    name?: string;
    phone?: string;
    email?: string;
  } | null;
  uploads: CanvasFile[];
}

interface CanvasOrdersResponse {
  success: boolean;
  data: CanvasOrder[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

const CanvasOrdersPage: React.FC<{ theme?: string }> = ({ theme = "light" }) => {
  const [orders, setOrders] = useState<CanvasOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<CanvasOrder | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [noteOrder, setNoteOrder] = useState<CanvasOrder | null>(null);
  const [repairNote, setRepairNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [previewFile, setPreviewFile] = useState<CanvasFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoadingId, setPreviewLoadingId] = useState("");
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<CanvasOrdersResponse>("/canvas-orders", {
        params: {
          page,
          pageSize,
          search: debouncedSearch || undefined,
        },
      });
      setOrders(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (fetchError) {
      setError(getErrorMessage(fetchError, "Canvas захиалга татаж чадсангүй"));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, pageSize]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search.trim());
    }, 350);

    return () => window.clearTimeout(timer);
  }, [search]);

  const changePage = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      return;
    }
    setPage(nextPage);
  };

  const uploadFiles = async () => {
    if (!selectedOrder || fileList.length === 0) return;

    const formData = new FormData();
    fileList.forEach((file) => {
      if (file.originFileObj) formData.append("files", file.originFileObj);
    });

    setUploading(true);
    try {
      await api.post(`/canvas-orders/${selectedOrder._id}/files`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      notification.success({
        message: "Зураг хадгалагдлаа",
        description: "Файлын чанар болон хэмжээ өөрчлөгдөөгүй.",
      });
      setFileList([]);
      setSelectedOrder(null);
      await fetchOrders();
    } catch (uploadError) {
      notification.error({
        message: "Upload амжилтгүй",
        description: getErrorMessage(uploadError, "Зураг upload хийж чадсангүй"),
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (file: CanvasFile) => {
    try {
      const response = await api.get(`/canvas-orders/files/${file._id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.originalName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      notification.success({
        message: "Таталт эхэллээ",
        description: file.originalName,
      });
    } catch (downloadError) {
      notification.error({
        message: "Файл татаж чадсангүй",
        description: getErrorMessage(downloadError, "Дахин оролдоно уу"),
      });
    }
  };

  const openPreview = async (file: CanvasFile) => {
    setPreviewLoadingId(file._id);
    try {
      const response = await api.get(
        `/canvas-orders/files/${file._id}/preview`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return url;
      });
      setPreviewFile(file);
    } catch (previewError) {
      notification.error({
        message: "Preview харуулж чадсангүй",
        description: getErrorMessage(previewError, "Дахин оролдоно уу"),
      });
    } finally {
      setPreviewLoadingId("");
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });
  };

  const deleteFile = (file: CanvasFile) => {
    Modal.confirm({
      title: "Зураг устгах уу?",
      content: file.originalName,
      okText: "Устгах",
      okButtonProps: { danger: true },
      cancelText: "Болих",
      onOk: async () => {
        try {
          await api.delete(`/canvas-orders/files/${file._id}`);
          notification.success({ message: "Зураг устгагдлаа" });
          await fetchOrders();
        } catch (deleteError) {
          notification.error({
            message: "Устгаж чадсангүй",
            description: getErrorMessage(deleteError, "Дахин оролдоно уу"),
          });
        }
      },
    });
  };

  const openRepairNote = (order: CanvasOrder) => {
    setNoteOrder(order);
    setRepairNote(order.canvasRepairNote || "");
  };

  const saveRepairNote = async () => {
    if (!noteOrder) return;

    setSavingNote(true);
    try {
      const response = await api.patch(
        `/canvas-orders/${noteOrder._id}/repair-note`,
        { repairNote }
      );
      const savedNote = response.data?.data?.canvasRepairNote || "";
      setOrders((current) =>
        current.map((order) =>
          order._id === noteOrder._id
            ? { ...order, canvasRepairNote: savedNote }
            : order
        )
      );
      setNoteOrder(null);
      notification.success({ message: "Засварын тайлбар хадгалагдлаа" });
    } catch (saveError) {
      notification.error({
        message: "Тайлбар хадгалж чадсангүй",
        description: getErrorMessage(saveError, "Дахин оролдоно уу"),
      });
    } finally {
      setSavingNote(false);
    }
  };

  const renderCanvasTags = (order: CanvasOrder) => (
    <Space size={[6, 6]} wrap>
      {order.canvas.map((item) => (
        <Tag key={item._id} color="cyan">
          {item.size || "Хэмжээгүй"} · Код: {item.code || "-"}
        </Tag>
      ))}
    </Space>
  );

  const renderFiles = (order: CanvasOrder) =>
    order.uploads.length ? (
      <div className="canvas-file-list">
        {order.uploads.map((file) => (
          <div className="canvas-file-row" key={file._id}>
            <div>
              <strong title={file.originalName}>{file.originalName}</strong>
              <small>{formatBytes(file.size)}</small>
            </div>
            <Space size={4}>
              <Button
                type="text"
                icon={<EyeOutlined />}
                loading={previewLoadingId === file._id}
                title="Preview харах"
                onClick={() => openPreview(file)}
              />
              <Button
                type="text"
                icon={<CloudDownloadOutlined />}
                title="Эх файлаар татах"
                onClick={() => downloadFile(file)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                title="Устгах"
                onClick={() => deleteFile(file)}
              />
            </Space>
          </div>
        ))}
      </div>
    ) : (
      <Tag>Зураг оруулаагүй</Tag>
    );

  const renderRepairNote = (order: CanvasOrder) => (
    <button
      type="button"
      className={`canvas-repair-note${
        order.canvasRepairNote ? " has-note" : ""
      }`}
      onClick={() => openRepairNote(order)}
    >
      <EditOutlined />
      <span>{order.canvasRepairNote || "Засварын тайлбар нэмэх"}</span>
    </button>
  );

  const columns: ColumnsType<CanvasOrder> = [
    {
      title: "Өдөр / цаг",
      key: "date",
      width: 130,
      render: (_, order) => (
        <div className="canvas-order-date">
          <strong>{order.year}-{order.day}</strong>
          <span>{order.bookedTime || "-"}</span>
        </div>
      ),
    },
    {
      title: "Захиалагч",
      key: "customer",
      width: 220,
      render: (_, order) => (
        <div className="canvas-order-customer">
          <strong>{order.customer?.name || "Нэр олдсонгүй"}</strong>
          <span>{order.customer?.phone || "Утас олдсонгүй"}</span>
          {order.customer?.email && <small>{order.customer.email}</small>}
        </div>
      ),
    },
    {
      title: "Canvas",
      key: "canvas",
      width: 260,
      render: (_, order) => renderCanvasTags(order),
    },
    {
      title: "Засварын тайлбар",
      key: "repairNote",
      width: 320,
      render: (_, order) => renderRepairNote(order),
    },
    {
      title: "Зураг файл",
      key: "uploads",
      width: 310,
      render: (_, order) => renderFiles(order),
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 145,
      fixed: "right",
      render: (_, order) => (
        <Button
          type="primary"
          icon={<CloudUploadOutlined />}
          onClick={() => setSelectedOrder(order)}
        >
          Зураг оруулах
        </Button>
      ),
    },
  ];

  return (
    <div className={`canvas-orders-page ${theme === "dark" ? "is-dark" : ""}`}>
      <div className="canvas-orders-header">
        <div>
          <Typography.Title level={2}>Canvas захиалга</Typography.Title>
          <Typography.Text type="secondary">
            Захиалагчийн мэдээлэл, canvas код болон эх зураг файлыг нэг дор удирдана.
          </Typography.Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchOrders} loading={loading}>
          Шинэчлэх
        </Button>
      </div>

      <div className="canvas-orders-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Нэр, утас, и-мэйл, canvas кодоор хайх"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <span>{total} захиалга</span>
      </div>

      {error && <Alert type="error" showIcon message={error} />}

      {isMobile ? (
        <>
          <List
            className="canvas-mobile-list"
            loading={loading}
            dataSource={orders}
            pagination={false}
            locale={{ emptyText: <Empty description="Canvas захиалга олдсонгүй" /> }}
            renderItem={(order) => (
            <List.Item>
              <Card
                size="small"
                title={
                  <div className="canvas-mobile-title">
                    <strong>{order.customer?.name || "Нэр олдсонгүй"}</strong>
                    <span>
                      {order.year}-{order.day} · {order.bookedTime || "-"}
                    </span>
                  </div>
                }
              >
                <dl className="canvas-mobile-details">
                  <div>
                    <dt>Холбоо барих</dt>
                    <dd>
                      <span>{order.customer?.phone || "Утас олдсонгүй"}</span>
                      {order.customer?.email && (
                        <small>{order.customer.email}</small>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Canvas</dt>
                    <dd>{renderCanvasTags(order)}</dd>
                  </div>
                  <div>
                    <dt>Засварын тайлбар</dt>
                    <dd>{renderRepairNote(order)}</dd>
                  </div>
                  <div>
                    <dt>Зураг файл</dt>
                    <dd>{renderFiles(order)}</dd>
                  </div>
                </dl>
                <Button
                  block
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  onClick={() => setSelectedOrder(order)}
                >
                  Зураг оруулах
                </Button>
              </Card>
            </List.Item>
            )}
          />
          <Pagination
            className="canvas-server-pagination"
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={[25, 50, 100]}
            showTotal={(value) => `Нийт ${value} захиалга`}
            onChange={changePage}
          />
        </>
      ) : (
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={orders}
          scroll={{ x: 1385 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100],
            showTotal: (value) => `Нийт ${value} захиалга`,
            onChange: changePage,
          }}
          locale={{ emptyText: <Empty description="Canvas захиалга олдсонгүй" /> }}
        />
      )}

      <Modal
        title="Эх зураг upload хийх"
        open={Boolean(selectedOrder)}
        onCancel={() => {
          if (!uploading) {
            setSelectedOrder(null);
            setFileList([]);
          }
        }}
        okText="Upload хийх"
        cancelText="Болих"
        onOk={uploadFiles}
        okButtonProps={{ disabled: fileList.length === 0, loading: uploading }}
        cancelButtonProps={{ disabled: uploading }}
        destroyOnClose
      >
        {selectedOrder && (
          <div className="canvas-upload-summary">
            <strong>{selectedOrder.customer?.name || "Нэр олдсонгүй"}</strong>
            <span>{selectedOrder.customer?.phone || "Утас олдсонгүй"}</span>
            <span>
              Код: {selectedOrder.canvas.map((item) => item.code || "-").join(", ")}
            </span>
          </div>
        )}
        <Upload.Dragger
          multiple
          accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.tif,.tiff"
          beforeUpload={() => false}
          fileList={fileList}
          onChange={({ fileList: nextList }) => setFileList(nextList.slice(0, 20))}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Зургаа энд чирэх эсвэл дарж сонгоно уу</p>
          <p className="ant-upload-hint">
            Нэг файл 60MB хүртэл, нийт 20 зураг. Файлыг шахахгүй, хэмжээг өөрчлөхгүй.
          </p>
        </Upload.Dragger>
      </Modal>

      <Modal
        className="canvas-preview-modal"
        title={previewFile?.originalName || "Зургийн preview"}
        open={Boolean(previewFile)}
        onCancel={closePreview}
        footer={null}
        width={920}
        destroyOnClose
      >
        {previewUrl && (
          <img
            className="canvas-preview-image"
            src={previewUrl}
            alt={previewFile?.originalName || "Canvas preview"}
          />
        )}
      </Modal>

      <Modal
        title="Засварын тайлбар"
        open={Boolean(noteOrder)}
        onCancel={() => {
          if (!savingNote) setNoteOrder(null);
        }}
        okText="Хадгалах"
        cancelText="Болих"
        onOk={saveRepairNote}
        okButtonProps={{ loading: savingNote }}
        cancelButtonProps={{ disabled: savingNote }}
        destroyOnClose
      >
        {noteOrder && (
          <div className="canvas-upload-summary">
            <strong>{noteOrder.customer?.name || "Нэр олдсонгүй"}</strong>
            <span>{noteOrder.customer?.phone || "Утас олдсонгүй"}</span>
            <span>
              Canvas:{" "}
              {noteOrder.canvas
                .map(
                  (item) =>
                    `${item.size || "Хэмжээгүй"} · ${item.code || "-"}`
                )
                .join(", ")}
            </span>
          </div>
        )}
        <Input.TextArea
          value={repairNote}
          onChange={(event) => setRepairNote(event.target.value)}
          placeholder="Жишээ: Нүүрний толбо цэвэрлэх, арын фон солих, өнгийг дулаан болгох"
          autoSize={{ minRows: 5, maxRows: 10 }}
          maxLength={2000}
          showCount
        />
      </Modal>
    </div>
  );
};

export default CanvasOrdersPage;
